// Task 8 — katalog birim alanları için yaz-süz combobox'ı (varsayılan birim + alternatif ekleme).
// APG "editable combobox with list autocomplete": odak girişte kalır, seçenekler
// `aria-activedescendant` ile; aile başlıklı liste (`unitPickerGroups`), yazdıkça süzülür, listede
// olmayan metin son satırda "Diğer birim: …" olarak sunulur (serbest metin yine mümkün). Enter
// formu ASLA göndermez; blur'da yazılan metin kaybolmaz. Esc yalnız açık listeyi kapatır ve
// yayılmaz (alttaki KpiModal kapanmasın). Mevcut `UnitSelect` tüketicileri bu bileşenden etkilenmez.

import { useId, useMemo, useState, type CSSProperties } from "react";
import { normalizeUnitKey } from "@mahir/shared";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import { comboboxKey } from "../../lib/kpi-unit-combobox-keys";
import { unitPickerGroups } from "../../lib/kpi-unit-picker";
import type { UnitOptionGroup } from "../../lib/kpi-unit-options";
import { inputStyle } from "./kpi-form-styles";
import { UnitListbox, flattenUnitGroups } from "./UnitSelect";

const CB = KPI_UNIT_COPY.combobox;

export interface UnitComboboxProps {
  id?: string;
  ariaLabel: string;
  /** giriş metni (kontrollü) */
  value: string;
  onChange: (text: string) => void;
  /** listeden seçim / Enter (yazılan metinle) — verilmezse yalnız onChange */
  onCommit?: (unit: string) => void;
  /** varsayılan birim: aynı aile önce + katsayı ipucu */
  related?: string | null;
  exclude?: readonly string[];
  placeholder?: string;
  disabled?: boolean;
}

/** Süzülmüş gruplar + (yazılan metin listede yoksa) son "Diğer birim: …" satırı. */
export function comboboxGroups(args: {
  related: string | null;
  exclude: readonly string[];
  query: string;
}): UnitOptionGroup[] {
  const groups = unitPickerGroups({
    related: args.related,
    exclude: args.exclude,
    query: args.query,
  });
  const typed = args.query.trim();
  const listed = groups.some((g) =>
    g.options.some((o) => normalizeUnitKey(o.value) === normalizeUnitKey(typed)),
  );
  if (typed !== "" && !listed) {
    groups.push({
      key: "other",
      label: KPI_UNIT_COPY.groups.other,
      options: [{ value: typed, label: CB.other(typed), hint: null }],
    });
  }
  return groups;
}

export function UnitCombobox(props: UnitComboboxProps): JSX.Element {
  const { value, onChange, onCommit, disabled } = props;
  const gen = useId();
  const listId = `${gen}-list`;
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false); // kullanıcı yazdı → süz; yoksa tam liste
  const [active, setActive] = useState(-1);
  const groups = useMemo(
    () =>
      comboboxGroups({
        related: props.related ?? null,
        exclude: props.exclude ?? [],
        query: dirty ? value : "",
      }),
    [props.related, props.exclude, dirty, value],
  );
  const flat = useMemo(() => flattenUnitGroups(groups), [groups]);
  const selectedIndex = flat.findIndex(
    (o) => value.trim() !== "" && normalizeUnitKey(o.value) === normalizeUnitKey(value),
  );

  function pick(index: number): void {
    const o = flat[index];
    if (!o) return;
    onChange(o.value);
    setDirty(false);
    setOpen(false);
    setActive(-1);
    onCommit?.(o.value);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    const r = comboboxKey(e.key, { open, active, selectedIndex });
    if (r.preventDefault) e.preventDefault();
    if (r.stopPropagation) e.stopPropagation();
    const a = r.action;
    if (a.type === "open") {
      setOpen(true);
      setActive(a.active);
    } else if (a.type === "move") {
      const last = flat.length - 1;
      setActive((cur) =>
        Math.max(0, Math.min(last, (cur < 0 ? (a.delta > 0 ? -1 : last + 1) : cur) + a.delta)),
      );
    } else if (a.type === "pick") {
      pick(a.index);
    } else if (a.type === "commit") {
      setOpen(false);
      if (value.trim() !== "") onCommit?.(value.trim());
    } else if (a.type === "close") {
      setOpen(false);
      setActive(-1);
    }
  }

  return (
    <div style={{ position: "relative", minWidth: 0, flex: "1 1 200px", maxWidth: 260 }}>
      <input
        id={props.id}
        role="combobox"
        aria-label={props.ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        {...(open ? { "aria-controls": listId } : {})}
        {...(open && active >= 0
          ? { "aria-activedescendant": `${listId}-${active.toString()}` }
          : {})}
        autoComplete="off"
        style={{ ...inputStyle, width: "100%", minHeight: 44 }}
        value={value}
        maxLength={32}
        disabled={disabled}
        placeholder={props.placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setDirty(true);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => {
          setOpen(false); // yazılan metin kalır
          setDirty(false);
        }}
        onKeyDown={onKeyDown}
      />
      {open && flat.length > 0 ? (
        <div style={pop}>
          <UnitListbox
            id={listId}
            groups={groups}
            activeIndex={active}
            selectedIndex={selectedIndex}
            ariaLabel={CB.listLabel}
            onPick={pick}
            onHover={setActive}
          />
        </div>
      ) : null}
    </div>
  );
}

const pop: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  top: "100%",
  marginTop: 4,
  zIndex: 60,
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  boxShadow: "0 10px 30px rgba(0,0,0,0.14)",
  padding: 6,
  minWidth: 240,
};
